import { AfterViewInit, Component, ElementRef, ViewChild, OnInit, Output, EventEmitter, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { RectifyDefectsService } from 'src/app/services/rectify-defects/rectify-defects.service';
import { CoreService } from 'src/app/services/core/core.service';
import { SubmitResolvedDefects } from 'src/app/models/rectify-defects.model';
import { clearActiveFocus } from 'src/app/helpers/dom/focus.util';

@Component({
  selector: 'app-defect-digital-signaturepad',
  templateUrl: './defect-digital-signaturepad.component.html',
  styleUrls: ['./defect-digital-signaturepad.component.scss'],
  standalone: false
})
export class DefectDigitalSignaturepadComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('signatureCanvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;
  @Input() existingSignature: string | null = null;
  @Output() existingSignatureChange = new EventEmitter<string | null>();
  @Output() signatureChanged = new EventEmitter<void>();
  @Input() lockLandscapeOnMobile: boolean = false;
  isSubmitted = false;
  selectedDefects!: SubmitResolvedDefects;
  errorPayload: string[] = [];
  private ctx!: CanvasRenderingContext2D;
  private isDrawing = false;
  private listenersAttached = false;

  constructor(
    private readonly router: Router,
    private readonly rectifyDefectService: RectifyDefectsService,
    private readonly coreService: CoreService) {
  }

  get isMobileApp() {
    return this.coreService.isMobileApp;
  }

  async ngOnInit() {
    this.selectedDefects = this.rectifyDefectService.getResolvedDefectDetail();

  }

  async ngAfterViewInit() {
    this.selectedDefects =
      this.rectifyDefectService.getResolvedDefectDetail();

    await new Promise(resolve =>
      requestAnimationFrame(() =>
        requestAnimationFrame(resolve)
      )
    );

    this.initCanvas();
  }

  ngOnDestroy(): void {
    this.removeCanvasListeners();
  }


  private initCanvas() {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    this.ctx = canvas.getContext('2d')!;
    this.ctx.scale(dpr, dpr);

    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.strokeStyle = 'black';
    this.ctx.lineWidth = 2.5;

    if (!this.listenersAttached) {
      this.addCanvasListeners();
      this.listenersAttached = true;
    }
  }

  private addCanvasListeners() {
    const canvas = this.canvasRef.nativeElement;

    canvas.addEventListener('pointerdown', this.startDrawing);
    canvas.addEventListener('pointermove', this.draw);
    canvas.addEventListener('pointerup', this.stopDrawing);
    canvas.addEventListener('pointerleave', this.stopDrawing);
    canvas.addEventListener('pointercancel', this.stopDrawing);
  }

  private removeCanvasListeners() {
    const canvas = this.canvasRef?.nativeElement;

    if (!canvas) return;

    canvas.removeEventListener('pointerdown', this.startDrawing);
    canvas.removeEventListener('pointermove', this.draw);
    canvas.removeEventListener('pointerup', this.stopDrawing);
    canvas.removeEventListener('pointerleave', this.stopDrawing);
    canvas.removeEventListener('pointercancel', this.stopDrawing);

    this.listenersAttached = false;
  }

  private startDrawing = (event: PointerEvent) => {
    this.isSubmitted = false;
    this.signatureChanged.emit();
    this.isDrawing = true;

    const canvas = this.canvasRef.nativeElement;
    canvas.setPointerCapture(event.pointerId);

    const pos = this.getRelativePosition(event);

    this.ctx.beginPath();
    this.ctx.moveTo(pos.x, pos.y);
  };

  private draw = (event: PointerEvent) => {
    if (!this.isDrawing) return;
    const pos = this.getRelativePosition(event);
    this.ctx.lineTo(pos.x, pos.y);
    this.ctx.stroke();
  };

  private stopDrawing = () => {
    this.isDrawing = false;
    this.ctx.beginPath();
  };

  public showSignatureValidationError() {
    this.isSubmitted = true;
  }

  private getRelativePosition(event: PointerEvent) {
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }

  clear() {
    this.existingSignatureChange.emit(null);

    const canvas = this.canvasRef.nativeElement;

    if (this.ctx) {
      this.ctx.save();

      this.ctx.setTransform(1, 0, 0, 1, 0, 0);

      this.ctx.clearRect(0, 0, canvas.width, canvas.height);

      this.ctx.restore();

      this.ctx.beginPath();
    }
    this.isSubmitted = false;
  }


  public getSignatureImage() {
    return this.canvasRef.nativeElement.toDataURL('image/png');
  }

  async save() {
    const canvas = this.canvasRef.nativeElement;
    const image = canvas.toDataURL('image/png');

    // Check if blank
    if (this.isCanvasBlank(canvas)) {
      this.isSubmitted = true;
      return;
    }

    const base64 = image.split(',')[1];
    const mimeType = image.match(/data:(.*);base64/)?.[1] ?? '';

    this.selectedDefects.evidenceMediaContent = base64;
    this.selectedDefects.evidenceMediaMimeType = mimeType;
    this.rectifyDefectService.setResolvedDefectDetail(this.selectedDefects);

    clearActiveFocus();
    this.router.navigateByUrl('/rectify-detail-signature');
    this.isSubmitted = false;
  }

  public isCanvasBlank(canvas: HTMLCanvasElement): boolean {
    const blank = document.createElement('canvas');
    blank.width = canvas.width;
    blank.height = canvas.height;
    return canvas.toDataURL() === blank.toDataURL();
  }

}
